import React, { useEffect, useState } from 'react';

import { Modal } from '@wds/modal';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';

import PropTypes from 'prop-types';

import STRINGS from '../../constants';
import CustomField from '../../../sma.common/components/react/CustomField/CustomField';

const BecomeApplicantModal = (props) => {
	const {
		open,
		setOpen,
		handleOnSuccess,
	} = props;
	const [errors, setErrors] = useState({});
	const [requiredFields, setRequiredFields] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (open) {
			getRequiredCustomFields();
		} else {
			setRequiredFields([]);
		}
	}, [open]);

	const validateCustomFields = () => {
		const formErrors = {};
		requiredFields.forEach((field) => {
			if (field.required && (!field.value || field.value.length === 0)) {
				formErrors[field.id] = `${field.name} is required`;
			} else {
				delete formErrors[field.id];
			}
		});
		setErrors(Object.keys(formErrors).length ? { ...formErrors } : {});
		return Object.keys(formErrors).length === 0;
	};

	const handleClose = () => {
		setErrors({});
		setRequiredFields([]);
		setIsLoading(false);
		setOpen(false);
	};

	const getRequiredCustomFields = () => {
		setIsLoading(true);
		const processedCustomFields = [];
		let url = reverse('api-internal-custom-field-list');
		url += '?target=applicant&required=1';
		const request = new Request(url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'X-No-Pagination': true,
			},
		});
		fetch(request)
			.then((response) => response.json())
			.then((data) => {
				if (data?.results) {
					data?.results.forEach((customField) => {
						const values = {
							id: customField.id,
							name: customField.name,
							type: customField.type,
							required: customField.required,
							readonly: customField.readonly,
							value: customField.default,
						};
						if (customField.type === 1) {
							values.choices = customField.choices;
						}
						processedCustomFields.push(values);
					});
				}
				setRequiredFields(processedCustomFields);
			});
		setIsLoading(false);
	};

	const handleCustomFieldChange = (id) => (value) => {
		const fieldIndex = requiredFields.findIndex((field) => field.id === id);
		const updatedFields = [...requiredFields];
		updatedFields[fieldIndex].value = value;
		setRequiredFields(updatedFields);
	};

	const handleSave = () => {
		if (validateCustomFields()) {
			handleOnSuccess(requiredFields);
		}
	};

	return (
		<Box
			id='sma-disconnect-account-modal'
			role='dialog'
			aria-labelledby='modal-title'
			aria-describedby='modal-description'
		>
			<Modal size='md' show={open} onClose={handleClose}>
				<Modal.Header
					id='modal-title'
					header={STRINGS.accountSettingsPage.modals.BecomeApplicantModal.title}
				/>
				<Modal.Body>
					<Box mt={5}>
						{isLoading && (
							<Box mb={5} role='status' aria-live='polite'>
								<ProgressCircle continuous />
							</Box>
						)}
						<Box mb={5} display='flex' flexDirection='column' gap={2}>
							{!isLoading && requiredFields.length > 0 && (
								<Typography variant='bodySm' lineHeight={1.5}>
									{STRINGS.accountSettingsPage.modals.BecomeApplicantModal.bodyCfsRequired}
								</Typography>
							)}
							<Box
								id='custom-fields'
								display='flex'
								gap={2}
								flexDirection='column'
								role='group'
								aria-labelledby='custom-fields-label'
							>
								<Typography
									id='custom-fields-label'
									variant='bodySm'
									lineHeight={1.5}
								>
									{STRINGS.accountSettingsPage.modals.BecomeApplicantModal.customFieldsLabel}
								</Typography>
								{requiredFields.map((field) => (
									<CustomField
										onChange={handleCustomFieldChange(field.id)}
										key={`custom-field-${field.id}`}
										id={field.id}
										label={field.name}
										value={field.value || field.default}
										name={field.name}
										type={field.type}
										required={field.required}
										choices={field.choices}
										errors={errors}
										setErrors={setErrors}
										helperText={field.helper_text ? field.helper_text : ''}
										defaultValue={field.default}
										aria-required={field.required}
										aria-invalid={!!errors[field.id]}
										aria-describedby={errors[field.id] ? `${field.id}-error` : undefined}
									/>
								))}
							</Box>
						</Box>
					</Box>
				</Modal.Body>
				<Modal.Footer align='right'>
					<Button
						color='muted'
						onClick={handleClose}
						id='sma-modal-cancel-button'
						variant='secondary'
						aria-label='Cancel and close the modal'
					>
						{STRINGS.cancel}
					</Button>
					<Button
						onClick={handleSave}
						id='sma-modal-save-button'
						aria-label='Save and submit the form'
					>
						{STRINGS.accountSettingsPage.modals.BecomeApplicantModal.title.toUpperCase()}
					</Button>
				</Modal.Footer>
			</Modal>
		</Box>
	);
};

BecomeApplicantModal.propTypes = {
	open: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired,
	handleOnSuccess: PropTypes.func.isRequired,
};
BecomeApplicantModal.defaultProps = { customFields: {} };

export default BecomeApplicantModal;
